import {
  Client as ChatClient,
  State as ChatClientState,
  Participant as ChatParticipant,
  Conversation,
  Message,
} from '@twilio/conversations';
import { atom } from 'jotai';
import { atomFamily } from 'jotai/utils';
import { SyncClient } from 'twilio-sync';

import { createDerivedWritableAtom } from '../utils';
import { LocalMediaStatus, isScreenShareModeAtom, localScreenShareContextAtom } from '../video-sdk';
import { twilioIdentityAtom } from '../video-sdk/twilio';
import { ConnectionError, ConnectionState, ConnectionStateStatus } from './types';
import { chatParticipantToName, getChatParticipants } from './utils';

export type ConversationsBySid = Record<string, Conversation>;
export type MessagesByConversationSid = Record<string, Message[]>;
export type ChatParticipantsById = Record<string, ChatParticipant>;
export type ParticipantIdsByConversationSid = Record<string, Set<string>>;
export type UnreadMessageCountByConversationSid = Record<string, number>;

export type TwilioChatSyncAtom = {
  chatClientConnectionState?: ConnectionStateStatus | null;
  chatClientState?: ChatClientState | null;
  chatParticipantsById: ChatParticipantsById;
  conversationsBySid: ConversationsBySid;
  currentConversationSid?: string;
  messagesByConversationSid: MessagesByConversationSid;
  participantIdsByConversationSid: ParticipantIdsByConversationSid;
  syncClientConnectionError?: ConnectionError | null;
  syncClientConnectionState?: ConnectionStateStatus;
  twilioChatClient?: ChatClient | null;
  twilioChatConnectionAttemptCount: number;
  twilioSyncClient?: SyncClient;
  twilioSyncConnectionAttemptCount: number;
  unreadMessageCountByConversationSid: Record<string, number>;
};

export const DEFAULT_TWILIO_CHAT_SYNC_ATOM_VALUE = {
  chatParticipantsById: {},
  conversationsBySid: {},
  messagesByConversationSid: {},
  participantIdsByConversationSid: {},
  twilioChatConnectionAttemptCount: 0,
  twilioSyncConnectionAttemptCount: 0,
  unreadMessageCountByConversationSid: {},
};

export const twilioChatSyncAtom = atom<TwilioChatSyncAtom>(DEFAULT_TWILIO_CHAT_SYNC_ATOM_VALUE);

// This is its own atom because opening it in Jotai's dev tools causes the dev
// tools/page to freeze up
export const twilioChatClientAtom = atom<ChatClient | null>(null);

export const conversationsBySidAtom = createDerivedWritableAtom(twilioChatSyncAtom, 'conversationsBySid');

export const messagesByConversationSidAtom = createDerivedWritableAtom(twilioChatSyncAtom, 'messagesByConversationSid');

export const chatParticipantsByIdAtom = createDerivedWritableAtom(twilioChatSyncAtom, 'chatParticipantsById');

export const participantIdsByConversationSidAtom = createDerivedWritableAtom(
  twilioChatSyncAtom,
  'participantIdsByConversationSid',
);

export const unreadMessageCountByConversationSidAtom = createDerivedWritableAtom(
  twilioChatSyncAtom,
  'unreadMessageCountByConversationSid',
);

export const unreadMessagesCountFamily = atomFamily((conversationSid: string) => {
  return atom((get) => {
    const unreadMessageCountByConversationSid = get(unreadMessageCountByConversationSidAtom);
    return unreadMessageCountByConversationSid[conversationSid] ?? 0;
  });
});

export const currentConversationSidAtom = createDerivedWritableAtom(twilioChatSyncAtom, 'currentConversationSid');

export const currentConversationAtom = atom((get) => {
  const currentConversationSid = get(currentConversationSidAtom);
  if (!currentConversationSid) return;
  const conversations = get(conversationsBySidAtom);
  return conversations[currentConversationSid];
});

export const currentConversationMessagesAtom = atom((get) => {
  const currentConversationSid = get(currentConversationSidAtom);
  if (!currentConversationSid) return [];
  return get(messagesByConversationSidAtom)[currentConversationSid] ?? [];
});

export const currentConversationParticipantIdsAtom = atom((get) => {
  const currentConversationSid = get(currentConversationSidAtom);
  if (!currentConversationSid) return new Set<string>();
  return get(participantIdsByConversationSidAtom)[currentConversationSid] ?? new Set<string>();
});

export const currentConversationParticipantsAtom = atom((get) => {
  const participantIds = get(currentConversationParticipantIdsAtom);
  const chatParticipants = get(chatParticipantsByIdAtom);
  const twilioIdentity = get(twilioIdentityAtom);
  return getChatParticipants(participantIds, chatParticipants, twilioIdentity);
});

export const currentConversationParticipantNamesAtom = atom((get) => {
  const currentConversationParticipants = get(currentConversationParticipantsAtom);
  return Array.from(currentConversationParticipants).map(chatParticipantToName);
});

export const twilioSyncClientAtom = createDerivedWritableAtom(twilioChatSyncAtom, 'twilioSyncClient');

export const syncClientConnectionStateAtom = createDerivedWritableAtom(twilioChatSyncAtom, 'syncClientConnectionState');

export const syncClientConnectionErrorAtom = createDerivedWritableAtom(twilioChatSyncAtom, 'syncClientConnectionError');

export const chatClientConnectionStateAtom = createDerivedWritableAtom(twilioChatSyncAtom, 'chatClientConnectionState');

export const chatClientStateAtom = createDerivedWritableAtom(twilioChatSyncAtom, 'chatClientState');

const badChatStates = new Set<ConnectionStateStatus>([
  ConnectionState.ERROR,
  ConnectionState.DISCONNECTED,
  ConnectionState.DENIED,
  ConnectionState.UNKNOWN,
]);

export const isChatErrorAtom = atom((get) => {
  const chatClientState = get(chatClientConnectionStateAtom);
  return !!chatClientState && badChatStates.has(chatClientState);
});

export const isChatReadyAtom = atom((get) => get(twilioChatClientAtom) && get(chatClientStateAtom) === 'initialized');

export const twilioSyncConnectionAttemptCountAtom = createDerivedWritableAtom(
  twilioChatSyncAtom,
  'twilioSyncConnectionAttemptCount',
);

export const twilioChatConnectionAttemptCountAtom = createDerivedWritableAtom(
  twilioChatSyncAtom,
  'twilioChatConnectionAttemptCount',
);

export const isMediaShareModeAtom = atom((get) => {
  const { status } = get(localScreenShareContextAtom);
  const isScreenShareModeLoading = status === LocalMediaStatus.LOADING;
  const isScreenShareMode = get(isScreenShareModeAtom);

  return isScreenShareModeLoading || isScreenShareMode;
});
